import type { ComponentPropsWithoutRef, ReactElement } from "react";

import { useSelector } from "react-redux";

import { useTranslate } from "@similarweb/i18n-provider";
import { IconButton } from "@similarweb/ui-components";

import OpportunityListUpdatedModalContainer from "app/pages/sales-intelligence/common-components/OpportunityListUpdatedModal/OpportunityListUpdatedModalContainer";
import type { TableActionProps } from "app/pages/sales-intelligence/common-components/table-actions-panel";
import SaveToListTooltipContent from "app/pages/sales-intelligence/modules/leads-search/containers/leads-actions/SaveToListButton/SaveToListTooltipContent";
import SaveToListButtonBaseContainer from "app/pages/sales-intelligence/sub-modules/opportunities/containers/SaveToListButton/SaveToListButtonBaseContainer";
import useTrackedWebsitesQuota from "app/pages/sales-intelligence/sub-modules/opportunities/hooks/useTrackedWebsitesQuota";
import { openHooksModalV5 } from "app/services/ModalService";

import { useClearAndCloseRowsSelection } from "../../../hooks/useClearAndCloseRowsSelection";
import { selectActiveLeadsType, selectIsTableRowExpanded } from "../../../store/selectors";

import { BUTTON_I18N_KEYS, MAX_WIDTH_TOOLTIP } from "./const";
import type { I18nKeyModifier } from "./types";

type ButtonProps = ComponentPropsWithoutRef<typeof SaveToListButtonBaseContainer>;

type Props = {
    isDisabled: boolean;
    i18nKeyModifier: I18nKeyModifier;
    onStayOnPage: VoidFunction;
    onGoToList: VoidFunction;
    onHookClick?: VoidFunction;
} & TableActionProps &
    Omit<ButtonProps, "renderTooltipContent">;

const SaveToListButtonContainer = ({
    isDisabled,
    iconName,
    i18nKeyModifier,
    onClick,
    onGoToList,
    onStayOnPage,
    onHookClick,
}: Props) => {
    const { translate } = useTranslate();
    const { isOutOfQuota } = useTrackedWebsitesQuota();

    const isTableRowExpanded = useSelector(selectIsTableRowExpanded);
    const activeLeadsType = useSelector(selectActiveLeadsType);

    const clearSelectionAndClosePanel = useClearAndCloseRowsSelection();

    const modalTitleKey = BUTTON_I18N_KEYS[activeLeadsType].modalKey(i18nKeyModifier);
    const buttonLabelKey = BUTTON_I18N_KEYS[activeLeadsType].buttonKey(i18nKeyModifier);

    const renderTooltipContent = (): ReactElement => (
        <SaveToListTooltipContent
            isInnerTableVisible={isTableRowExpanded}
            i18nKeyModifier={i18nKeyModifier}
        />
    );

    if (isOutOfQuota) {
        return (
            <IconButton
                type="flatTrial"
                iconName="sparkles"
                onClick={() => {
                    onHookClick?.();
                    openHooksModalV5("SalesSolutionLists");
                }}
            >
                {translate(buttonLabelKey)}
            </IconButton>
        );
    }

    return (
        <>
            <OpportunityListUpdatedModalContainer
                title={translate(modalTitleKey)}
                onStayOnPage={onStayOnPage}
                onGoToList={onGoToList}
                onClose={clearSelectionAndClosePanel}
            />
            <SaveToListButtonBaseContainer
                isDisabled={isDisabled || isTableRowExpanded}
                isTooltipDisabled={isDisabled}
                btnText={translate(buttonLabelKey)}
                iconName={iconName}
                maxWidthTooltip={MAX_WIDTH_TOOLTIP}
                onClick={onClick}
                renderTooltipContent={renderTooltipContent}
            />
        </>
    );
};

export default SaveToListButtonContainer;
